import CreditCardTermModal from "@/components/CreditCardTermModal";
import CvvModal from "@/components/CvvModal";
import {
  ExpirationDateField,
  SingleCheckboxField,
  TextField,
} from "@/components/Form/Parts/index";
import { useModal } from "@/hooks";
import formStyles from "@/styles/Form.module.scss";
import paymentSelectStyles from "@/styles/PaymentSelect.module.scss";
import classnames from "classnames";
import { useCallback, useRef } from "react";
import IconHelp from "../../../svgs/icon_help.svg";

export default function CreditCardInput({
  register,
  errors,
  setValue,
  visible,
}) {
  if (!visible) return null;
  const { showModal } = useModal();

  const bsDelKey = useRef();

  const handleKeyDown = useCallback((e) => {
    bsDelKey.current = e.code === "Backspace" || e.code === "Delete";
  });

  const handleInputCardNumber = useCallback((e) => {
    if (bsDelKey.current) return false;
    const digit = e.target.value.replace(/[^\d]+/g, "");

    // ブランド判別
    const chunkMap = (() => {
      if (/^(34|37)/.test(digit)) {
        // Amex 4-6-5
        return [
          [0, 4],
          [4, 10],
          [10, 15],
        ];
      } else if (/^(30|36|38|39)/.test(digit)) {
        // Diners 4-6-4
        return [
          [0, 4],
          [4, 10],
          [10, 14],
        ];
      } else {
        // Other 4-4-4-4
        return [
          [0, 4],
          [4, 8],
          [8, 12],
          [12, 16],
        ];
      }
    })();

    const value = chunkMap.reduce((acc, n, i) => {
      const [start, end] = n;
      const chnkValue = digit.slice(start, end);
      acc = acc + chnkValue;
      if (i !== chunkMap.length - 1 && chnkValue.length === end - start) {
        acc = acc + "-";
      }
      return acc;
    }, "");

    e.target.value = value;
  }, []);

  const handleInputCvv = useCallback((e) => {
    if (!/^\d{1,16}$/.test(e.target.value)) {
      e.target.value = e.target.value.replace(/[^\d]+/g, "");
    }
  }, []);

  const handleCvvBtnClick = useCallback(() => {
    showModal(<CvvModal />, {
      closeButton: false,
      outerAreaClose: false,
      style: "dialogue",
    });
  }, []);

  const handleCreditCardTermBtnClick = useCallback(() => {
    showModal(<CreditCardTermModal />, {
      closeButton: false,
      outerAreaClose: false,
      style: "creditCardTermModal",
    });
  }, []);

  return (
    <div className={paymentSelectStyles.addCreditCardSection}>
      <header className={paymentSelectStyles.header}>
        <h4 className={paymentSelectStyles.heading}>クレジットカード情報</h4>
      </header>
      <div className={paymentSelectStyles.creditCardInfoContainer}>
        <div className={paymentSelectStyles.creditCardNumberField}>
          <TextField
            {...register("cardNumber")}
            title="クレジットカード番号"
            placeholder="半角数字でカード番号を入力"
            error={errors.cardNumber?.message}
            required
            className={paymentSelectStyles.creditCardTextInput}
            onInput={handleInputCardNumber}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className={paymentSelectStyles.creditCardExpirationDateField}>
          <div>
            <ExpirationDateField
              {...register("expirationDate")}
              title="有効期限"
              error={errors.expirationDate?.message}
              setValue={setValue}
              required
            />
          </div>
        </div>
        <div className={paymentSelectStyles.creditCardCvvField}>
          <legend
            className={classnames(formStyles.formTitle, {
              [formStyles.required]: true,
            })}
          >
            セキュリティコード
            <a
              onClick={handleCvvBtnClick}
              className={paymentSelectStyles.iconHelp}
            >
              <IconHelp />
            </a>
          </legend>
          <TextField
            {...register("cvv")}
            inputMode="numeric"
            placeholder="半角数字で入力"
            error={errors.cvv?.message}
            onInput={handleInputCvv}
            maxLength="4"
            required
          />
        </div>
      </div>
      <br />
      <div className={paymentSelectStyles.creditCardInfoContainer}>
        <div className={paymentSelectStyles.creditCard3dsField}>
          <legend
            className={classnames(formStyles.formTitle, {
              [formStyles.required]: true,
            })}
          >
            カード名義（英字名）
          </legend>
          <TextField
            {...register("firstName")}
            placeholder="カード名義（英字名）"
            error={errors.firstName?.message}
            required
          />
        </div>
        <div className={paymentSelectStyles.creditCard3dsField}>
          <legend
            className={classnames(formStyles.formTitle, {
              [formStyles.required]: true,
            })}
          >
            カード名義（英字姓）
          </legend>
          <TextField
            {...register("lastName")}
            placeholder="カード名義（英字姓）"
            error={errors.lastName?.message}
            required
          />
        </div>
        <div className={paymentSelectStyles.creditCard3dsEmailField}>
          <legend
            className={classnames(formStyles.formTitle, {
              [formStyles.required]: true,
            })}
          >
            メールアドレス
          </legend>
          <TextField
            {...register("mailAddress")}
            placeholder="半角英数字でメールアドレスを入力"
            error={errors.mailAddress?.message}
            required
          />
        </div>
      </div>
      <div className={paymentSelectStyles.creditCardTermField}>
        <div className={paymentSelectStyles.creditCardTermText}>
          <a
            onClick={handleCreditCardTermBtnClick}
            className={paymentSelectStyles.creditCardTermBtn}
          >
            「利用上の注意および個人情報の第三者提供について」
          </a>
          をお読みいただき、同意のうえお手続きしてください。
        </div>
        <div className={paymentSelectStyles.creditCardTermInput}>
          <SingleCheckboxField
            {...register("creditCardTerm")}
            title=""
            defaultChecked={false}
            label={"上記の内容について確認・同意しました"}
            error={errors?.creditCardTerm?.message}
          />
        </div>
      </div>
    </div>
  );
}
